'use client'

import { Card, Image } from '@vinted/web-ui'
import { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { clickEvent, userViewItemGallery } from '@marketplace-web/item-page/item-page-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { ItemPhotoDto } from '../types'

type Props = {
  photos: Array<ItemPhotoDto>
  selectedPhotoId: number
  setSelectedPhotoId: (photoId: number) => void
  target: Parameters<typeof clickEvent>[0]['target']
  itemId: number
  imageView: 'fullscreen' | 'item_page'
}

const Thumbnails = ({
  photos,
  selectedPhotoId,
  setSelectedPhotoId,
  target,
  itemId,
  imageView,
}: Props) => {
  const containerRef = useRef<HTMLUListElement>(null)
  const { track } = useTracking()
  const translate = useTranslate('item.gallery.a11y')

  useEffect(() => {
    const thumbnail = containerRef.current?.children[selectedPhotoId] as HTMLElement
    containerRef.current?.scrollTo({
      top: thumbnail?.offsetTop ?? 0,
      behavior: 'smooth',
    })
  }, [selectedPhotoId])

  const handleThumbnailView = (index: number) => (inView: boolean) => {
    if (!inView) return

    track(
      userViewItemGallery({
        itemId,
        imagePosition: index + 1,
        isLoop: true,
        imageCount: photos.length,
        contentType: 'gallery_thumbnail',
        galleryContainsAd: false,
        imageView,
      }),
    )
  }

  const handleThumbnailClick = (index: number) => {
    setSelectedPhotoId(index)

    track(
      clickEvent({
        target,
        targetDetails: JSON.stringify({
          position: index + 1,
          item_id: itemId,
        }),
      }),
    )
  }

  return (
    <ul className="item-photos-thumbnails-container" ref={containerRef}>
      {photos.map((photo, index) => (
        <InView
          as="li"
          key={photo.id}
          className={classNames('item-photo-thumbnail', {
            'item-photo-thumbnail--active': index === selectedPhotoId,
          })}
          onChange={handleThumbnailView(index)}
          triggerOnce
        >
          <button
            type="button"
            onClick={() => handleThumbnailClick(index)}
            aria-current={index === selectedPhotoId ? 'true' : undefined}
            aria-label={translate('current_order_position', {
              position: index + 1,
              total: photos.length,
            })}
          >
            <Card>
              <Image
                src={photo.url}
                alt={`Thumbnail ${index + 1}`}
                size="x-large"
                ratio="small-portrait"
              />
            </Card>
          </button>
        </InView>
      ))}
    </ul>
  )
}

export default Thumbnails
